/**
 * Pure trajectory math for the care plan builder.
 *
 * Given a SelectedGoal (currentScore, targetScore, baselineMonths,
 * goalStartMonth, goalEndMonth, _overrides), fill out the trajectory
 * array: a baseline hold of `baselineMonths` months at currentScore,
 * then a linear ramp to targetScore at the final month, with anchor
 * points wherever the user has pinned a value via `_overrides`.
 *
 * Extracted from main.ts as part of OMON-031 Phase 2. All four
 * functions mutate only their SelectedGoal argument — no module
 * globals, no DOM. Tested directly in trajectory.test.ts.
 */

import type { SelectedGoal } from "../types";

/** Goal's own month count (endMonth - startMonth). */
export function goalMonths(obj: SelectedGoal): number {
  return obj.goalEndMonth - obj.goalStartMonth;
}

/** Resize trajectory array when plan or goal duration changes, preserving overrides where possible. */
export function resizeTrajectory(obj: SelectedGoal) {
  const len = goalMonths(obj);
  if (!obj.trajectory) obj.trajectory = [];
  if (!obj._overrides) obj._overrides = new Set();
  // Trim overrides beyond new length
  for (const idx of [...obj._overrides]) {
    if (idx >= len) obj._overrides.delete(idx);
  }
  if (obj.trajectory.length === len) return;
  // Resize: extend with 0 or truncate
  const old = obj.trajectory;
  obj.trajectory = new Array(len).fill(0);
  for (let i = 0; i < Math.min(old.length, len); i++) obj.trajectory[i] = old[i];
  buildTrajectory(obj);
}

export function ensureTrajectory(obj: SelectedGoal): number[] {
  const len = goalMonths(obj);
  if (!obj.trajectory || obj.trajectory.length !== len) {
    obj.trajectory = new Array(len).fill(0);
    if (!obj._overrides) obj._overrides = new Set();
    buildTrajectory(obj);
  }
  return obj.trajectory;
}

export function buildTrajectory(obj: SelectedGoal) {
  const t = obj.trajectory;
  const len = t.length;
  const last = len - 1;
  const bl = obj.baselineMonths || 2;
  const overrides = obj._overrides || new Set<number>();

  t[0] = obj.currentScore;
  t[last] = obj.targetScore;

  // Baseline hold
  for (let i = 1; i < bl && i < last; i++) {
    if (!overrides.has(i)) t[i] = obj.currentScore;
  }

  // Clear non-overridden ramp months
  for (let i = bl; i < last; i++) {
    if (!overrides.has(i)) t[i] = 0;
  }

  // Build anchor list
  const anchors: Array<{ i: number; v: number }> = [];
  const rampStart = Math.max(0, bl - 1);
  anchors.push({ i: rampStart, v: t[rampStart] !== 0 ? t[rampStart] : obj.currentScore });

  for (let i = rampStart + 1; i < last; i++) {
    if (t[i] !== 0) anchors.push({ i, v: t[i] });
  }
  anchors.push({ i: last, v: obj.targetScore });

  // Linear interpolation between anchors
  for (let a = 0; a < anchors.length - 1; a++) {
    const from = anchors[a];
    const to = anchors[a + 1];
    for (let i = from.i + 1; i < to.i; i++) {
      if (t[i] === 0 && !overrides.has(i)) {
        const ratio = (i - from.i) / (to.i - from.i);
        t[i] = Math.round(from.v + ratio * (to.v - from.v));
      }
    }
  }
}
